/**
 * Death ragdolls (IDEAS X33 stage 1; owner, Oct 10: "limit it to death ragdolls from explosions, and maybe collapse
 * with minor force death ragdoll for bullet death. No getting up at this stage."). Presentation only: a dying person's
 * model hands over to a jointed body — fifteen points on the person model's own skeleton (head, neck, shoulders,
 * elbows, hands, pelvis, hips, knees, feet) held by sticks, integrated Verlet-style — that a blast throws (up and
 * away from the burst, flailing) or a bullet only nudges (a slump where it stood), and that settles on the drawn
 * ground. Each frame the points drive the model's own groups, so the body is the soldier as drawn. Nothing is
 * written back: the match never reads where a body lies.
 *
 * Gravity is the fall's own, not the match's: at person scale the felt gravity is some 550× Earth's in body terms (a
 * body would hit the ground in 30 ms), so a death falls at three Earth gravities, in body heights, to be seen.
 * `CannedFall` is the budget's cheap fall (a rigid topple about the feet) for when too many ragdolls are live, and
 * for the browser smoke's lean frames. `bakeCorpse` folds a settled body into one mesh: one draw per corpse.
 *
 * 67.58, semi-rigid (the owner: "semi-rigid ragdoll would be cool but not sure if expensive (perf) or hard"), still the
 * same fifteen points:
 * - **joint limits**: a knee bends forward of the hip-to-foot line and an elbow never points forward (hinges), the
 *   head within a cone off the spine, a thigh and an upper arm within their ranges in the torso's frame (the torso stays
 *   one piece: the model's torso is one part);
 * - **a little stiffness**: soft sticks across each joint pull it back toward the pose the person died in, so a thrown
 *   body keeps a shape in the air and still gives on landing;
 * - **limbs meet the ground along their length**: a bone's middle is held off the ground too (a leg across a crest
 *   no longer sinks into it), not only its ends;
 * - **cheaper ground**: each point's ground is read once a substep and carried through the iterations (along the fall's
 *   up), so the ground query (the planet's surface in play) runs 48 times a frame, not 240.
 * `{ semiRigid: false }` is the 67.23 body (the sheet's compare).
 */
import * as THREE from 'three';
import type { PersonModel } from './personModel';

/** Where a body falls: the ground's normal there, the height of a point above the drawn ground, a person's height (world units). */
export interface DeathWorld {
  up: THREE.Vector3;
  heightAbove(p: THREE.Vector3): number;
  personHeight: number;
}

/** What killed it: a burst at `from` (`strength` 0–1), a round travelling along `dir`, or nothing to push (a fall, a hazard). */
export type DeathPush =
  | { kind: 'blast'; from: THREE.Vector3; strength: number }
  | { kind: 'bullet'; dir: THREE.Vector3 }
  | { kind: 'collapse' };

/** The fall's gravity in body heights a second² (≈3× Earth, as a body sees it). */
export const DEATH_GRAVITY = 18;
/** A ragdoll settles when no point moves faster than this (body heights a second) for SETTLE_HOLD seconds, or at most after MAX_SECONDS. */
const SETTLE_SPEED = 0.25, SETTLE_HOLD = 0.4, MAX_SECONDS = 5;
const DAMP = 0.992, FRICTION = 0.55, ITERATIONS = 8;

const JOINTS = ['head', 'neck', 'shL', 'shR', 'elL', 'elR', 'haL', 'haR', 'pelvis', 'hipL', 'hipR', 'knL', 'knR', 'ftL', 'ftR'] as const;
type Joint = typeof JOINTS[number];
const IDX = Object.fromEntries(JOINTS.map((j, i) => [j, i])) as Record<Joint, number>;
/** The sticks: the limbs' bones, and the torso and head braced so they keep their shape. */
const STICKS: Array<[Joint, Joint]> = [
  ['shL', 'elL'], ['elL', 'haL'], ['shR', 'elR'], ['elR', 'haR'],
  ['hipL', 'knL'], ['knL', 'ftL'], ['hipR', 'knR'], ['knR', 'ftR'],
  ['neck', 'shL'], ['neck', 'shR'], ['shL', 'shR'], ['neck', 'pelvis'],
  ['pelvis', 'hipL'], ['pelvis', 'hipR'], ['hipL', 'hipR'],
  ['shL', 'hipL'], ['shR', 'hipR'], ['shL', 'hipR'], ['shR', 'hipL'], ['neck', 'hipL'], ['neck', 'hipR'],
  ['head', 'neck'], ['head', 'shL'], ['head', 'shR'],
  ['hipL', 'ftL'], ['hipR', 'ftR'],   // soft: a leg never folds flat on itself (see LEG_MIN)
];
/** The two hip-to-foot sticks only stop a leg folding beyond this share of its length (a knee bends, but not to nothing). */
const LEG_MIN = 0.55;
/**
 * 67.58: the soft sticks across each joint (elbows, knees, shoulders, hips, the neck), their rest the pose at the death;
 * `STIFFNESS` the share of a soft stick's error taken back each iteration (the hard sticks take it all).
 */
const SOFT: Array<[Joint, Joint]> = [
  ['shL', 'haL'], ['shR', 'haR'], ['hipL', 'ftL'], ['hipR', 'ftR'],
  ['neck', 'elL'], ['neck', 'elR'], ['hipR', 'knL'], ['hipL', 'knR'],
  ['head', 'pelvis'],
];
/** The neck's head sticks: hard in the 67.23 body, soft (the neck gives, inside its cone) in the semi-rigid one. */
const NECK_STICKS = new Set([STICKS.length - 4, STICKS.length - 3]);   // head–shL, head–shR
/** The limits and the stiffness (67.58). Angles as their sines and cosines, in the torso's frame (forward, up, out). */
export const RAGDOLL_JOINTS = {
  /** the soft sticks' pull, a share of their error each iteration */
  stiffness: 0.035,
  /** a knee bends forward only, its plane turned at most this far (tan) off the body's forward with the hip's turn */
  kneeTurn: 1.0,
  /** the head within this cone off the spine (cos 40°) */
  neckCone: 0.766,
  /** a thigh's range: back at most −0.45 (≈27°), up at most 0.5 (≈120° of bend), out at most 0.75, across at most −0.3 */
  thigh: { back: -0.45, up: 0.5, out: 0.75, across: -0.3 },
  /** an upper arm's range: back at most −0.8, across the chest at most −0.35 (up is free: an arm goes overhead) */
  upperArm: { back: -0.8, across: -0.35 },
};
/** The bones whose middles meet the ground (67.58): the limbs and the spine. */
const BONES: Array<[Joint, Joint]> = [
  ['shL', 'elL'], ['elL', 'haL'], ['shR', 'elR'], ['elR', 'haR'],
  ['hipL', 'knL'], ['knL', 'ftL'], ['hipR', 'knR'], ['knR', 'ftR'], ['neck', 'pelvis'],
];
const RADIUS: Partial<Record<Joint, number>> = { head: 0.09, pelvis: 0.08, neck: 0.07 };   // body heights; the rest 0.05

const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _m = new THREE.Matrix4(), _inv = new THREE.Matrix4();
const _F = new THREE.Vector3(), _U = new THREE.Vector3(), _A = new THREE.Vector3(), _d = new THREE.Vector3(), _o = new THREE.Vector3(), _f = new THREE.Vector3(), _s = new THREE.Vector3();

/** The model's joints in world space, from its posed groups. */
function jointsOf(m: PersonModel): THREE.Vector3[] {
  m.root.updateMatrixWorld(true);
  const at = (o: THREE.Object3D, x = 0, y = 0, z = 0) => o.localToWorld(new THREE.Vector3(x, y, z));
  const p: Record<Joint, THREE.Vector3> = {
    head: at(m.head, 0, 0.16, 0), neck: at(m.head), shL: at(m.armL.group), shR: at(m.armR.group),
    elL: at(m.armL.fore), elR: at(m.armR.fore), haL: at(m.armL.fore, 0, -m.armL.b, 0), haR: at(m.armR.fore, 0, -m.armR.b, 0),
    pelvis: at(m.hips), hipL: at(m.thighL), hipR: at(m.thighR), knL: at(m.shinL), knR: at(m.shinR), ftL: at(m.footL), ftR: at(m.footR),
  };
  return JOINTS.map(j => p[j]);
}

/**
 * Set a group's world transform: origin at `pos`, its +Y along `y`, its +X as near `x` as square allows, at the model's
 * scale `s`. 67.58: a negative `s` is a right-hander (67.21: the model mirrored across its centreline, the root's x scale
 * flipped) — the mirror lives on the part's z, so its +X and +Y stay where the joints put them (a uniform −s turned every
 * part of a right-hander 180° about its own x: the torso hung under the pelvis, the head under the neck, the forearms
 * folded back up the arm — since 67.21 met 67.23).
 */
function placeWorld(o: THREE.Object3D, pos: THREE.Vector3, y: THREE.Vector3, x: THREE.Vector3, s: number): void {
  const Y = _a.copy(y).normalize();
  const X = _b.copy(x).addScaledVector(Y, -x.dot(Y));
  if (X.lengthSq() < 1e-12) X.set(1, 0, 0).addScaledVector(Y, -Y.x);
  X.normalize();
  const Z = new THREE.Vector3().crossVectors(X, Y);
  const k = Math.abs(s);
  _m.makeBasis(X.multiplyScalar(k), Y.clone().multiplyScalar(k), Z.multiplyScalar(s)).setPosition(pos);
  o.parent!.updateMatrixWorld();
  _inv.copy(o.parent!.matrixWorld).invert();
  o.matrixAutoUpdate = false;
  o.matrix.multiplyMatrices(_inv, _m);
  o.matrixWorld.copy(_m);
}

/** Pose the model's groups from the joints (parents before children). */
function applyJoints(m: PersonModel, p: THREE.Vector3[], s: number): void {
  const J = (j: Joint) => p[IDX[j]];
  const up = new THREE.Vector3().subVectors(J('neck'), J('pelvis'));
  const across = new THREE.Vector3().subVectors(J('shR'), J('shL'));
  const hipsAcross = new THREE.Vector3().subVectors(J('hipR'), J('hipL'));
  placeWorld(m.hips, J('pelvis'), up, hipsAcross, s);
  placeWorld(m.torso, J('pelvis'), up, across, s);
  placeWorld(m.head, J('neck'), new THREE.Vector3().subVectors(J('head'), J('neck')), across, s);
  placeWorld(m.armL.group, J('shL'), new THREE.Vector3().subVectors(J('shL'), J('elL')), across, s);
  placeWorld(m.armL.fore, J('elL'), new THREE.Vector3().subVectors(J('elL'), J('haL')), across, s);
  placeWorld(m.armR.group, J('shR'), new THREE.Vector3().subVectors(J('shR'), J('elR')), across, s);
  placeWorld(m.armR.fore, J('elR'), new THREE.Vector3().subVectors(J('elR'), J('haR')), across, s);
  const shinL = new THREE.Vector3().subVectors(J('knL'), J('ftL')), shinR = new THREE.Vector3().subVectors(J('knR'), J('ftR'));
  placeWorld(m.thighL, J('hipL'), new THREE.Vector3().subVectors(J('hipL'), J('knL')), hipsAcross, s);
  placeWorld(m.shinL, J('knL'), shinL, hipsAcross, s);
  placeWorld(m.footL, J('ftL'), shinL, hipsAcross, s);
  placeWorld(m.thighR, J('hipR'), new THREE.Vector3().subVectors(J('hipR'), J('knR')), hipsAcross, s);
  placeWorld(m.shinR, J('knR'), shinR, hipsAcross, s);
  placeWorld(m.footR, J('ftR'), shinR, hipsAcross, s);
  m.root.updateMatrixWorld(true);   // the groups' meshes follow
}

export interface Falling { step(dt: number): boolean; readonly settled: boolean }

/** 67.58: the ragdoll's build — semi-rigid (the default) or the 67.23 body. */
export interface RagdollOptions { semiRigid?: boolean }

/** How many ground queries the ragdolls have made (a probe's count: 67.58's cost). */
export const ragdollStats = { groundQueries: 0 };

export class DeathRagdoll implements Falling {
  private readonly p: THREE.Vector3[];
  private readonly prev: THREE.Vector3[];
  private readonly rest: number[];
  private readonly s: number;
  private readonly softRest: number[];
  private readonly semi: boolean;
  /** each point's ground along up (its height along up less its height above the ground), read once a substep */
  private readonly level: number[];
  private age = 0;
  private calm = 0;
  settled = false;

  constructor(private readonly model: PersonModel, private readonly world: DeathWorld, push: DeathPush, rnd: () => number = Math.random, opts: RagdollOptions = {}) {
    this.s = model.root.scale.x;
    this.semi = opts.semiRigid ?? true;
    this.p = jointsOf(model);
    this.rest = STICKS.map(([a, b]) => this.p[IDX[a]].distanceTo(this.p[IDX[b]]));
    this.softRest = SOFT.map(([a, b]) => this.p[IDX[a]].distanceTo(this.p[IDX[b]]));
    this.level = this.p.map(() => 0);
    const ph = world.personHeight, up = world.up;
    const v = this.p.map(() => new THREE.Vector3());
    const spin = () => new THREE.Vector3(rnd() - 0.5, rnd() - 0.5, rnd() - 0.5);
    if (push.kind === 'blast') {
      // thrown up and away from the burst, harder the nearer a part is to it, each part flailing its own way
      const away = new THREE.Vector3().subVectors(this.p[IDX.pelvis], push.from);
      away.addScaledVector(up, -away.dot(up));
      if (away.lengthSq() < 1e-12) away.set(1, 0, 0).addScaledVector(up, -up.x);
      away.normalize();
      const speed = (3 + 5 * Math.max(0, Math.min(1, push.strength))) * ph;
      const near = this.p.map(q => q.distanceTo(push.from));
      const nearest = Math.min(...near), farthest = Math.max(...near);
      this.p.forEach((_, i) => {
        const k = 0.85 + 0.35 * (farthest > nearest ? (farthest - near[i]) / (farthest - nearest) : 0.5);
        v[i].copy(away).multiplyScalar(speed * k).addScaledVector(up, speed * 0.7 * k).addScaledVector(spin(), 3 * ph);
      });
    } else if (push.kind === 'bullet') {
      // a nudge at the chest along the round; the knees give; the body slumps where it stood
      const d = push.dir.clone().addScaledVector(up, -push.dir.dot(up)).normalize();
      for (const j of ['head', 'neck', 'shL', 'shR'] as const) v[IDX[j]].copy(d).multiplyScalar(1.4 * ph);
      for (const j of ['knL', 'knR'] as const) v[IDX[j]].copy(d).multiplyScalar(-0.6 * ph);
    } else {
      for (const j of ['knL', 'knR', 'head'] as const) v[IDX[j]].copy(spin()).multiplyScalar(0.8 * ph);
    }
    const dt0 = 1 / 60;
    this.prev = this.p.map((q, i) => q.clone().addScaledVector(v[i], -dt0));
    model.aim.visible = false;            // the gun in hand drops (it has no body of its own yet)
    model.controller.visible = false;
  }

  /** Advance the fall; true once the body has settled (it then holds its pose). */
  step(dt: number): boolean {
    if (this.settled) return true;
    const ph = this.world.personHeight, up = this.world.up;
    const h = Math.min(Math.max(dt, 0), 1 / 30) / 2;
    let fastest = 0;
    for (let sub = 0; sub < 2; sub++) {
      const g = up.clone().multiplyScalar(-DEATH_GRAVITY * ph * h * h);
      for (let i = 0; i < this.p.length; i++) {
        const q = this.p[i], pr = this.prev[i];
        const vx = (q.x - pr.x) * DAMP, vy = (q.y - pr.y) * DAMP, vz = (q.z - pr.z) * DAMP;
        pr.copy(q);
        q.set(q.x + vx + g.x, q.y + vy + g.y, q.z + vz + g.z);
      }
      // 67.58: each point's ground read once a substep, carried along up through the iterations
      if (this.semi) for (let i = 0; i < this.p.length; i++) { this.level[i] = this.p[i].dot(up) - this.world.heightAbove(this.p[i]); ragdollStats.groundQueries++; }
      for (let it = 0; it < ITERATIONS; it++) {
        STICKS.forEach(([a, b], si) => {
          const pa = this.p[IDX[a]], pb = this.p[IDX[b]];
          const d = _a.subVectors(pb, pa), len = d.length();
          if (len < 1e-12) return;
          const isLeg = si >= STICKS.length - 2;
          if (isLeg && len >= this.rest[si] * LEG_MIN) return;   // a leg only resists folding too far
          const target = isLeg ? this.rest[si] * LEG_MIN : this.rest[si];
          const k = this.semi && NECK_STICKS.has(si) ? 0.15 : 1;   // 67.58: the neck gives (its cone holds it)
          const corr = d.multiplyScalar(((len - target) / len) * 0.5 * k);
          pa.add(corr); pb.sub(corr);
        });
        if (this.semi) { this.soften(); this.limit(); }
        for (let i = 0; i < this.p.length; i++) {
          const q = this.p[i], r = (RADIUS[JOINTS[i]] ?? 0.05) * ph;
          const under = r - (this.semi ? q.dot(up) - this.level[i] : (ragdollStats.groundQueries++, this.world.heightAbove(q)));
          if (under <= 0) continue;
          q.addScaledVector(up, under);
          this.rub(i);
        }
        if (this.semi) this.bonesOnGround(it === 0);
      }
      for (let i = 0; i < this.p.length; i++) fastest = Math.max(fastest, this.p[i].distanceTo(this.prev[i]) / h);
    }
    applyJoints(this.model, this.p, this.s);
    this.age += 2 * h;
    this.calm = fastest < SETTLE_SPEED * ph ? this.calm + 2 * h : 0;
    if (this.calm >= SETTLE_HOLD || this.age >= MAX_SECONDS) this.settled = true;
    return this.settled;
  }

  /** On the ground: no bounce, and friction on the slide. */
  private rub(i: number): void {
    const up = this.world.up, q = this.p[i], pr = this.prev[i], vel = _b.subVectors(q, pr);
    vel.addScaledVector(up, -vel.dot(up)).multiplyScalar(FRICTION);
    pr.copy(q).sub(vel);
  }

  /** 67.58: the soft sticks, a share of their error each iteration — the joints lean back toward the death's pose. */
  private soften(): void {
    const k = RAGDOLL_JOINTS.stiffness * 0.5;
    for (let si = 0; si < SOFT.length; si++) {
      const pa = this.p[IDX[SOFT[si][0]]], pb = this.p[IDX[SOFT[si][1]]];
      const d = _a.subVectors(pb, pa), len = d.length();
      if (len < 1e-12) continue;
      const corr = d.multiplyScalar(((len - this.softRest[si]) / len) * k);
      pa.add(corr); pb.sub(corr);
    }
  }

  /**
   * 67.58: the joints' limits, in the torso's frame (forward, up along the spine, across to the right): each moves the
   * child point only (a knee, an elbow, the head) back inside its range, at its bone's length.
   */
  private limit(): void {
    const P = this.p, J = RAGDOLL_JOINTS;
    const U = _U.subVectors(P[IDX.neck], P[IDX.pelvis]); if (U.lengthSq() < 1e-12) return; U.normalize();
    const A = _A.subVectors(P[IDX.shR], P[IDX.shL]); A.addScaledVector(U, -A.dot(U)); if (A.lengthSq() < 1e-12) return; A.normalize();
    // the model faces +Z with its left arm at −x: forward = across × up; a right-hander is mirrored (its left arm at +x
    // in the world's terms), so its forward is the other way
    const F = _F.crossVectors(A, U).multiplyScalar(this.s < 0 ? -1 : 1);
    // the head in its cone off the spine
    this.cone(IDX.neck, IDX.head, U, J.neckCone);
    // hips and shoulders: the thigh and the upper arm clamped in (forward, up, out), at their length
    for (const side of [-1, 1]) {
      const out = side;   // left −1: its out is −across
      this.range(side < 0 ? IDX.hipL : IDX.hipR, side < 0 ? IDX.knL : IDX.knR, F, U, A, out, J.thigh.back, J.thigh.up, J.thigh.out, J.thigh.across);
      this.range(side < 0 ? IDX.shL : IDX.shR, side < 0 ? IDX.elL : IDX.elR, F, U, A, out, J.upperArm.back, Infinity, Infinity, J.upperArm.across);
      // knees forward of the hip-to-foot line; elbows never forward of the shoulder-to-hand line
      this.hinge(side < 0 ? IDX.hipL : IDX.hipR, side < 0 ? IDX.knL : IDX.knR, side < 0 ? IDX.ftL : IDX.ftR, F, 1, J.kneeTurn);
      this.hinge(side < 0 ? IDX.shL : IDX.shR, side < 0 ? IDX.elL : IDX.elR, side < 0 ? IDX.haL : IDX.haR, F, -1, Infinity);
    }
  }

  /** The child `c` of `a` within a cone of cosine `cos` about `axis`, at its distance. */
  private cone(a: number, c: number, axis: THREE.Vector3, cos: number): void {
    const v = _d.subVectors(this.p[c], this.p[a]), len = v.length();
    if (len < 1e-12) return;
    const along = v.dot(axis) / len;
    if (along >= cos) return;
    const side = _o.copy(v).addScaledVector(axis, -v.dot(axis));
    if (side.lengthSq() < 1e-12) side.set(1, 0, 0).addScaledVector(axis, -axis.x);
    side.normalize();
    _m3.copy(this.p[a]).addScaledVector(axis, cos * len).addScaledVector(side, Math.sqrt(1 - cos * cos) * len);
    this.share(c, [a], _m3);
  }

  /**
   * Move point `c` toward `to`, half the way, and its parents `from` the other half between them the opposite way: equal
   * and opposite, so a limit turns a joint without pushing the body along (a one-sided fix is a push under Verlet: the
   * body crept along the ground).
   */
  private share(c: number, from: number[], to: THREE.Vector3): void {
    const dx = (to.x - this.p[c].x) * 0.5, dy = (to.y - this.p[c].y) * 0.5, dz = (to.z - this.p[c].z) * 0.5, k = 1 / from.length;
    this.p[c].set(this.p[c].x + dx, this.p[c].y + dy, this.p[c].z + dz);
    for (const a of from) this.p[a].set(this.p[a].x - dx * k, this.p[a].y - dy * k, this.p[a].z - dz * k);
  }

  /** A bone from `a` to its child `c` clamped in the torso's frame: its forward share ≥ `back`, up ≤ `upMax`, out (by `out`'s side) ≤ `outMax` and ≥ `across`. */
  private range(a: number, c: number, F: THREE.Vector3, U: THREE.Vector3, A: THREE.Vector3, out: number, back: number, upMax: number, outMax: number, across: number): void {
    const v = _d.subVectors(this.p[c], this.p[a]), len = v.length();
    if (len < 1e-12) return;
    let f = v.dot(F) / len, u = v.dot(U) / len, o = out * v.dot(A) / len;
    const f0 = f, u0 = u, o0 = o;
    f = Math.max(f, back); u = Math.min(u, upMax); o = Math.min(Math.max(o, across), outMax);
    if (f === f0 && u === u0 && o === o0) return;
    const n = Math.hypot(f, u, o) || 1;
    _m3.copy(this.p[a]).addScaledVector(F, f / n * len).addScaledVector(U, u / n * len).addScaledVector(A, out * o / n * len);
    this.share(c, [a], _m3);
  }

  /**
   * A hinge at `k` between `a` and `c`: the joint's offset from the line a→c points along `dir` × `F` (a knee forward,
   * an elbow back) — never against it — and turned off it at most `turn` (tan) sideways.
   */
  private hinge(a: number, k: number, c: number, F: THREE.Vector3, dir: number, turn: number): void {
    const A = this.p[a], K = this.p[k], d = _d.subVectors(this.p[c], A), dd = d.lengthSq();
    if (dd < 1e-12) return;
    const t = Math.max(0, Math.min(1, _o.subVectors(K, A).dot(d) / dd));
    const base = _s.copy(A).addScaledVector(d, t), o = _o.subVectors(K, base);
    const fwd = _f.copy(F).multiplyScalar(dir).addScaledVector(d, -F.dot(d) * dir / dd);
    if (fwd.lengthSq() < 1e-12) return;
    fwd.normalize();
    const of = o.dot(fwd);
    const sideV = _a.copy(o).addScaledVector(fwd, -of), os = sideV.length();
    const of2 = Math.max(0, of), os2 = turn === Infinity ? os : Math.min(os, of2 * turn);
    if (of2 === of && os2 === os) return;
    _m3.copy(base).addScaledVector(fwd, of2);
    if (os > 1e-12) _m3.addScaledVector(sideV, os2 / os);
    this.share(k, [a, c], _m3);
  }

  /** 67.58: each bone's middle held off the ground (its ground read on the first iteration, carried along up after): both its ends lift. */
  private bonesOnGround(read: boolean): void {
    const up = this.world.up, ph = this.world.personHeight, r = 0.05 * ph;
    for (let b = 0; b < BONES.length; b++) {
      const ia = IDX[BONES[b][0]], ib = IDX[BONES[b][1]], pa = this.p[ia], pb = this.p[ib];
      const mid = _mid.addVectors(pa, pb).multiplyScalar(0.5);
      if (read || Number.isNaN(this.boneLevel[b])) { this.boneLevel[b] = mid.dot(up) - this.world.heightAbove(mid); ragdollStats.groundQueries++; }
      const under = r - (mid.dot(up) - this.boneLevel[b]);
      if (under <= 0) continue;
      pa.addScaledVector(up, under); pb.addScaledVector(up, under);
      this.rub(ia); this.rub(ib);
    }
  }
  private readonly boneLevel = new Array<number>(BONES.length).fill(NaN);
}
const _m3 = new THREE.Vector3(), _mid = new THREE.Vector3();

/**
 * The budget's fall: the body, as posed at the death, topples rigidly about its feet — away from a burst, along a
 * round, else backwards — accelerating like a fall, and lies there. Cheap: one rotation a frame.
 */
export class CannedFall implements Falling {
  private readonly pivot = new THREE.Group();
  private readonly axis: THREE.Vector3;
  private readonly base = new THREE.Quaternion();
  private t = 0;
  settled = false;
  private static readonly SECONDS = 0.55;

  constructor(model: PersonModel, world: DeathWorld, push: DeathPush, facing: THREE.Vector3) {
    model.root.updateMatrixWorld(true);
    const feet = new THREE.Vector3().addVectors(model.footL.getWorldPosition(new THREE.Vector3()), model.footR.getWorldPosition(new THREE.Vector3())).multiplyScalar(0.5);
    const up = world.up;
    let dir = push.kind === 'blast' ? new THREE.Vector3().subVectors(feet, push.from) : push.kind === 'bullet' ? push.dir.clone() : facing.clone().negate();
    dir.addScaledVector(up, -dir.dot(up));
    if (dir.lengthSq() < 1e-12) dir = new THREE.Vector3(1, 0, 0).addScaledVector(up, -up.x);
    dir.normalize();
    this.axis = new THREE.Vector3().crossVectors(up, dir).normalize();
    const holder = model.root.parent!;
    holder.parent!.add(this.pivot);
    this.pivot.position.copy(feet);
    this.pivot.updateMatrixWorld();
    this.pivot.attach(holder);   // keeps the body where it stood, now turning about its feet
    this.base.copy(this.pivot.quaternion);
    model.aim.visible = false;
    model.controller.visible = false;
  }

  step(dt: number): boolean {
    if (this.settled) return true;
    this.t = Math.min(CannedFall.SECONDS, this.t + Math.max(0, dt));
    const f = this.t / CannedFall.SECONDS;
    this.pivot.quaternion.copy(this.base).premultiply(new THREE.Quaternion().setFromAxisAngle(this.axis, (Math.PI / 2 - 0.08) * f * f));
    this.pivot.updateMatrixWorld(true);
    if (f >= 1) this.settled = true;
    return this.settled;
  }
}

/** One shared material for every corpse (vertex colours carry the parts' colours). */
let corpseMaterial: THREE.MeshLambertMaterial | null = null;
export function corpseMaterialShared(): THREE.MeshLambertMaterial {
  return corpseMaterial ??= new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });
}

/**
 * Fold a settled body into one mesh: every visible part, its colour carried in the vertices, positions relative to
 * `anchor` (kept small for float precision), placed at the anchor. One draw per corpse.
 */
export function bakeCorpse(root: THREE.Object3D, anchor: THREE.Vector3): THREE.Mesh {
  root.updateMatrixWorld(true);
  const pos: number[] = [], nor: number[] = [], col: number[] = [];
  const nm = new THREE.Matrix3(), v = new THREE.Vector3(), n = new THREE.Vector3(), c = new THREE.Color();
  root.traverseVisible(o => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry?.attributes.position) return;
    const geo = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry;
    const P = geo.attributes.position, N = geo.attributes.normal, C = geo.attributes.color;
    const mat = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.MeshLambertMaterial;
    const base = mat?.color ?? new THREE.Color(0xffffff);
    nm.getNormalMatrix(mesh.matrixWorld);
    for (let i = 0; i < P.count; i++) {
      v.fromBufferAttribute(P, i).applyMatrix4(mesh.matrixWorld).sub(anchor);
      pos.push(v.x, v.y, v.z);
      if (N) { n.fromBufferAttribute(N, i).applyMatrix3(nm).normalize(); nor.push(n.x, n.y, n.z); } else nor.push(0, 1, 0);
      if (C) { c.fromBufferAttribute(C, i); if (!(mat?.vertexColors)) c.multiply(base); } else c.copy(base);
      col.push(c.r, c.g, c.b);
    }
    if (geo !== mesh.geometry) geo.dispose();
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  const mesh = new THREE.Mesh(g, corpseMaterialShared());
  mesh.position.copy(anchor);
  return mesh;
}
